<template>
  <div
    class="tw-w-screen tw-max-w-full tw-h-screen tw-text-dark dark:tw-text-dm-dark dark:tw-bg-dm"
  >
    <template v-if="TEMP_local_storage_redirect_is_checked">
      <template v-if="tokenVerified && interceptorsPrepared && areTranslationsProcessed">
        <div
          v-if="me?.role !== $roles.FOREIGNER"
          :class="[isQuestionnairePage ? '' : 'tw-flex-1 tw-h-full tw-pl-[50px]', `${$route.name}`]"
        >
          <AdminAppNavV2 v-if="this['auth/loggedIn'] && !isQuestionnairePage" />
          <router-view
            :key="$route.fullPath"
            :class="[
              isQuestionnairePage ? 'tw-p-4 tw-w-full' : '',
              isMessagingPage || isQuestionnairePage ? '' : 'page tw-py-6 tw-px-10',isProfilePage ? 'tw-pb-0' : '']"
          />
        </div>

        <div
          v-else
          class="tw-flex"
        >
          <ForeignerAppNavV2 v-if="!isQuestionnairePage && !isMessagingPage" />
          <router-view
            :key="$route.fullPath"
            :class="[
              isQuestionnairePage ? 'tw-p-4 tw-w-full' : '',
              isMessagingPage || isQuestionnairePage ? '' : 'tw-flex-2 tw-px-20 tw-py-10 tw-pl-[140px] tw-h-screen tw-max-h-screen tw-overflow-auto',
              isProfilePage ? 'tw-pb-0 tw-h-full' : '',
            ]"
          />
          <ForeignerNotifications
            v-if="!(isMessagingPage || isQuestionnairePage || isFaqPage)"
            class="tw-flex-1 tw-border-l tw-border-gray dark:tw-border-dm-gray"
          />
          <ForeignerMessagingPopover v-if="!(isMessagingPage || isQuestionnairePage)" />
        </div>
      </template>
    </template>
    <template v-else>
      <router-view
        :key="$route.fullPath"
        class="page"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { authComputed } from '@/vuex/helpers.js';
import helpers from '@/helpers';
import { mapGetters, mapState } from 'vuex';
import ForeignerNotifications from '@/components/Foreigner/Notifications/ForeignerNotifications.vue';
import ForeignerMessagingPopover from '@/components/Foreigner/Messaging/ForeignerMessagingPopover.vue';
import ForeignerAppNavV2 from '@/components/ForeignerAppNavV2.vue';
import AdminAppNavV2 from '@/components/AdminAppNavV2.vue';

export default {
  name: 'App',
  components: {
    AdminAppNavV2,
    ForeignerAppNavV2,
    ForeignerMessagingPopover,
    ForeignerNotifications,
  },
  data() {
    return {
      tokenVerified: false,
      interceptorsPrepared: false,
      ifNoToken: window.localStorage.getItem('token') == null,
      LS_VERSION: import.meta.env.VITE_LS_VERSION,

      showOnlyMessaging: false,
      foreignerMessaging: null,
    };
  },
  computed: {
    ...authComputed,
    ...mapGetters({
      TEMP_local_storage_redirect_is_checked: 'coreData/TEMP_local_storage_redirect_is_checked',
    }),
    ...mapState({
      interfaceLanguages: (state) => state.v2.shared.enums.interface_languages,
      me: (state) => state.v2.shared.profile.me,
      currentTranslations: (state) => state.v2.shared.profile.translations,
      areTranslationsProcessed: (state) => state.v2.shared.profile.areTranslationsProcessed,
    }),
    isMessagingPage() {
      return this.$route.name === 'foreignersMessaging'
        || this.$route.name === 'foreignersMessagingPID'
        || this.$route.name === 'foreignersMessagingMID'
        || this.$route.name === 'foreignerViewMessaging'
        || this.$route.name === 'foreignerViewMessagingPID'
        || this.$route.name === 'foreignerViewMessagingMID'
        || this.$route.name === 'FamilyMessaging'
        || this.$route.name === 'FamilyMessagingPID'
        || this.$route.name === 'FamilyMessagingMID';
    },
    isQuestionnairePage() {
      return this.$route.name === 'AdminForeignersQuestionnaire' || this.$route.name === 'ForeignerForeignersQuestionnaire';
    },
    isFaqPage() {
      return this.$route.name === 'faq';
    },
    isProfilePage() {
      return this.$route.name === 'usersMe';
    },
  },

  watch: {
    ifNoToken: {
      handler() {
        if (!this.ifNoToken) {
          this.$store.dispatch('coreData/verifyLocalStorageRedirectIsChecked');
        }
      },
      immediate: true,
    },
    interfaceLanguages: {
      async handler(interfaceLanguages) {
        if (interfaceLanguages.length > 0) {
          await this.$store.dispatch('v2/shared/profile/loadMe');
        }
      },
      immediate: true,
    },
    'me.interface_language': {
      async handler(interfaceLanguageValue) {
        await this.$store.dispatch('v2/shared/profile/loadCurrentTranslations');
        const code = this.interfaceLanguages.find(
          (el) => el.id === interfaceLanguageValue,
        )?.name_text;
        localStorage.setItem('locale_code', code);
        const langData = { ...this.currentTranslations };
        for (const k in langData) {
          if (k !== 'main.login.a3' && k !== 'profile.out_of_office.message') {
            langData[k] = this.$sanitize(langData[k]);
          }
        }
        this.$i18n.mergeLocaleMessage(
          code,
          langData,
        );
        this.$i18n.locale = code;
        this.$store.dispatch('v2/shared/profile/confirmTranslationsProcessed');
      },
    },
  },
  async created() {
    if (!window.localStorage.getItem('locale_code')) {
      window.localStorage.setItem('locale_code', 'en');
    }
    this.$i18n.locale = window.localStorage.getItem('locale_code');

    await this.verifyToken().catch(() => {});

    const onRequestFulfilled = (config) => {
      config.headers['User-TZ'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const token = window.localStorage.getItem('token');
      if (token !== null) config.headers.Authorization = `JWT ${token}`;
      return config;
    };

    const onResponseFulfilled = (response) => {
      return response;
    };

    const onRequestRejected = (error) => {
      return Promise.reject(error);
    };

    const onResponseRejected = (currentAxios) => (error) => {
      this.$refreshCounter = this.$refreshCounter + 1;
      if (this.$refreshCounter > this.$refreshCounterLimit) {
        this.$store.dispatch('auth/logout');
      }
      if (error.message === 'Network Error') {
        return Promise.reject(error);
      }
      if (
        typeof error.response === 'undefined' &&
        error.message !== 'Network Error'
      ) {
        return this.$store
          .dispatch('auth/refreshToken', { location: 'A' })
          .then(({ data }) => {
            window.localStorage.setItem('token', data.token);
            currentAxios.defaults.headers.common['Authorization'] = `JWT ${data.token}`;
            return currentAxios.request(error.config);
          })
          .catch(() => {
            this.$refreshCounter++;
            if (this.$refreshCounter === this.$refreshCounterLimit) {
              this.$store.dispatch('auth/logout');
            }
          });
      }
      if (error.response.status === 401 && localStorage.getItem('token') !== null) {
        return this.$store
          .dispatch('auth/refreshToken', { location: 'B' })
          .then(({ data }) => {
            this.$refreshCounter = 0;
            localStorage.setItem('token', data.token);
            currentAxios.defaults.headers.common['Authorization'] = `JWT ${data.token}`;
            return currentAxios.request(error.config);
          })
          .catch(() => {
            this.$refreshCounter++;
            this.$store.dispatch('auth/logout');
          });
      }

      if(error.response.status === 400 && error.response.data.non_field_errors[0] === 'Refresh has expired.') {
        this.$store.dispatch('auth/logout');
      }
      return Promise.reject(error);
    };

    axios.interceptors.request.use(
      onRequestFulfilled,
      onRequestRejected,
    );

    axios.interceptors.response.use(
      onResponseFulfilled,
      onResponseRejected(axios),
    );

    this.$axios.interceptors.request.use(
      onRequestFulfilled,
      onRequestRejected,
    );

    this.$axios.interceptors.response.use(
      onResponseFulfilled,
      onResponseRejected(this.$axios),
    );

    this.interceptorsPrepared = true;

    const lsVersionNew = parseInt(this.LS_VERSION);
    let lsVersion = window.localStorage.getItem('itrack_ls_version');

    if (lsVersion === null) {
      helpers.removeLocalStorageFiltersRpp();
      window.localStorage.setItem('itrack_ls_version', String(lsVersionNew));
    } else {
      lsVersion = parseInt(lsVersion);
      if (lsVersion < lsVersionNew) {
        helpers.removeLocalStorageFiltersRpp();
        window.localStorage.setItem('itrack_ls_version', String(lsVersionNew));
      }
    }

    if (this.$store.getters['auth/loggedIn']) {
      await this.$store.dispatch('v2/shared/enums/loadInterfaceLanguages');
      await this.$store.dispatch('v2/shared/enums/loadEnums');
    }
  },
  methods: {
    async verifyToken() {
      return new Promise((resolve, reject) => {
        if (window.localStorage.getItem('token') !== null) {
          this.$store
            .dispatch('auth/verifyToken')
            .then(() => {
              this.tokenVerified = true;
              resolve();
            })
            .catch(() => {
              this.$store
                .dispatch('auth/refreshToken', { location: 'C' })
                .then(({ data }) => {
                  this.$refreshCounter = 0;
                  window.localStorage.setItem('token', data.token);
                  this.tokenVerified = true;
                  resolve();
                })
                .catch((err) => {
                  this.$refreshCounter = this.$refreshCounter + 1;
                  if (this.$refreshCounter > this.$refreshCounterLimit) {
                    this.$store.dispatch('auth/logout');
                  }
                  if (
                    err.response.status === 403 &&
                    err.response.data.message === 'Incorrect token'
                  ) {
                    this.$store.dispatch('auth/logout');
                  }
                });
            });
        } else {
          reject();
        }
      });
    },
  },
};
</script>
